type SectionLink = { link: HTMLAnchorElement; section: HTMLElement };
type AnalyticsParameters = Record<string, string>;
type CalendlyMessage = { event: string };
type AnalyticsWindow = Window & {
  gtag?: (command: "event", eventName: string, parameters?: AnalyticsParameters) => void;
};

function trackEvent(eventName: string, parameters: AnalyticsParameters): void {
  (window as AnalyticsWindow).gtag?.("event", eventName, parameters);
}

function isCalendlyMessage(data: unknown): data is CalendlyMessage {
  return typeof data === "object"
    && data !== null
    && "event" in data
    && typeof data.event === "string"
    && data.event.startsWith("calendly.");
}

const links = Array.from(document.querySelectorAll<HTMLAnchorElement>("[data-section-link]"));
const sectionLinks: SectionLink[] = links.flatMap((link) => {
  const section = document.querySelector<HTMLElement>(link.hash);
  return section ? [{ link, section }] : [];
});

function setActiveLink(activeSection: HTMLElement): void {
  sectionLinks.forEach(({ link, section }) => {
    const isActive = section === activeSection;
    link.classList.toggle("is-active", isActive);
    if (isActive) link.setAttribute("aria-current", "location");
    else link.removeAttribute("aria-current");
  });
}

const observer = new IntersectionObserver((entries) => {
  const visibleEntry = entries.filter((entry) => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
  if (visibleEntry?.target instanceof HTMLElement) setActiveLink(visibleEntry.target);
}, { rootMargin: "-20% 0px -65% 0px", threshold: [0, 0.1, 0.5] });

sectionLinks.forEach(({ section }) => observer.observe(section));

const menuButton = document.querySelector<HTMLButtonElement>("[data-mobile-menu-toggle]");
const mobileMenu = document.querySelector<HTMLElement>("#mobile-navigation");
const menuCloseButtons = document.querySelectorAll<HTMLButtonElement>("[data-mobile-menu-close]");
const menuBackdrop = document.querySelector<HTMLButtonElement>(".mobile-menu-backdrop");
const mobileHeader = document.querySelector<HTMLElement>(".mobile-header");
const mobileViewport = window.matchMedia("(max-width: 640px)");

function setMobileMenu(open: boolean, returnFocus = false): void {
  if (!menuButton || !mobileMenu) return;

  document.body.classList.toggle("is-mobile-menu-open", open);
  menuButton.setAttribute("aria-expanded", String(open));
  menuButton.setAttribute("aria-label", open ? "Close navigation" : "Open navigation");
  mobileMenu.setAttribute("aria-hidden", String(!open));
  menuBackdrop?.setAttribute("aria-hidden", String(!open));
  menuBackdrop?.setAttribute("tabindex", open ? "0" : "-1");
  if (!open && returnFocus) menuButton.focus();
}

menuButton?.addEventListener("click", () => {
  setMobileMenu(menuButton.getAttribute("aria-expanded") !== "true");
});

menuCloseButtons.forEach((button) => button.addEventListener("click", () => setMobileMenu(false, true)));

mobileMenu?.querySelectorAll<HTMLAnchorElement>("a").forEach((link) => {
  link.addEventListener("click", () => setMobileMenu(false));
});

document.addEventListener("keydown", (event) => {
  if (event.key === "Escape") setMobileMenu(false, true);
});

let lastScrollY = window.scrollY;
let scrollFramePending = false;

function updateMobileHeader(): void {
  if (!mobileHeader || !mobileViewport.matches) {
    mobileHeader?.classList.remove("is-hidden");
    return;
  }

  const currentScrollY = window.scrollY;
  const scrollDelta = currentScrollY - lastScrollY;

  if (currentScrollY <= 8 || scrollDelta < -6) mobileHeader.classList.remove("is-hidden");
  else if (scrollDelta > 6) mobileHeader.classList.add("is-hidden");

  lastScrollY = currentScrollY;
}

window.addEventListener("scroll", () => {
  if (scrollFramePending) return;

  scrollFramePending = true;
  window.requestAnimationFrame(() => {
    updateMobileHeader();
    scrollFramePending = false;
  });
}, { passive: true });

mobileViewport.addEventListener("change", () => {
  lastScrollY = window.scrollY;
  updateMobileHeader();
});

document.querySelectorAll<HTMLAnchorElement>("[data-track-email]").forEach((link) => {
  link.addEventListener("click", () => {
    trackEvent("email_click", {
      placement: link.dataset.analyticsLocation ?? "unknown"
    });
  });
});

let hasTrackedCalendlyTimeSelection = false;
let hasTrackedCalendlyBooking = false;

window.addEventListener("message", (message) => {
  const isCalendlyOrigin = message.origin === "https://calendly.com"
    || message.origin.endsWith(".calendly.com");

  if (!isCalendlyOrigin || !isCalendlyMessage(message.data)) return;

  if (message.data.event === "calendly.date_and_time_selected" && !hasTrackedCalendlyTimeSelection) {
    hasTrackedCalendlyTimeSelection = true;
    trackEvent("calendly_time_selected", { booking_location: "inline" });
  }

  if (message.data.event === "calendly.event_scheduled" && !hasTrackedCalendlyBooking) {
    hasTrackedCalendlyBooking = true;
    trackEvent("calendly_booking_completed", { booking_location: "inline" });
  }
});
